import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, Tag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { apiValidateCoupon, resolveImg } from "../api";

const Cart = () => {
  const { items, updateQty, removeFromCart, cartTotal, cartMrpTotal, clearCart } = useCart();
  const [coupon, setCoupon] = useState(localStorage.getItem("dg_coupon") || "");
  const [couponMsg, setCouponMsg] = useState("");
  const [discount, setDiscount] = useState(0);
  const navigate = useNavigate();

  const shipping = cartTotal > 999 || cartTotal === 0 ? 0 : 79;
  const grandTotal = cartTotal - discount + shipping;
  const savings = cartMrpTotal - cartTotal + discount;

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const res = await apiValidateCoupon(coupon.trim(), cartTotal);
      if (res.valid) {
        setDiscount(res.discountAmount);
        setCouponMsg(res.message);
        localStorage.setItem("dg_coupon", coupon.trim().toUpperCase());
      } else {
        setDiscount(0);
        setCouponMsg(res.message || "Invalid coupon");
        localStorage.removeItem("dg_coupon");
      }
    } catch {
      setCouponMsg("Could not validate coupon");
    }
  };

  if (items.length === 0) {
    return (
      <div className="py-28 text-center px-4">
        <ShoppingBag className="h-16 w-16 text-neutral-300 mx-auto" />
        <h1 className="font-serif text-3xl text-neutral-900 mt-6">Your cart is empty</h1>
        <p className="text-neutral-500 mt-2">Discover our finest fragrances and add your favourites.</p>
        <Link to="/collections/bestsellers"
          className="mt-8 inline-block bg-gradient-to-br from-[#FFE29A] to-[#C8932F] text-[#050304] px-8 py-3 text-sm font-semibold uppercase tracking-widest rounded-sm hover:opacity-90 transition-opacity">
          Shop Bestsellers
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1400px] px-4 lg:px-8 py-10">
      <h1 className="font-serif text-3xl text-neutral-900 mb-8">Shopping Cart</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {items.map((item) => (
            <div key={item.key} className="flex gap-4 bg-white border border-neutral-200 rounded-sm p-4">
              <Link to={`/products/${item.slug}`}>
                <img src={resolveImg(item.image)} alt={item.name} className="h-28 w-24 object-cover rounded-sm bg-[#f6f3ee]" />
              </Link>
              <div className="flex flex-1 flex-col">
                <div className="flex justify-between gap-2">
                  <div>
                    <Link to={`/products/${item.slug}`} className="font-semibold text-neutral-900 hover:text-[#C8932F]">{item.name}</Link>
                    <p className="text-xs text-neutral-500 mt-0.5">Size: {item.size}</p>
                  </div>
                  <button onClick={() => removeFromCart(item.key)} aria-label="Remove">
                    <Trash2 className="h-4 w-4 text-neutral-400 hover:text-[#D6425F]" />
                  </button>
                </div>
                <div className="mt-auto flex items-center justify-between pt-3">
                  <div className="flex items-center border border-neutral-300 rounded-sm">
                    <button onClick={() => updateQty(item.key, item.qty - 1)} className="px-2.5 py-1.5" aria-label="Decrease"><Minus className="h-3.5 w-3.5" /></button>
                    <span className="px-3 text-sm">{item.qty}</span>
                    <button onClick={() => updateQty(item.key, item.qty + 1)} className="px-2.5 py-1.5" aria-label="Increase"><Plus className="h-3.5 w-3.5" /></button>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-neutral-900">₹{(item.price * item.qty).toLocaleString("en-IN")}</span>
                    {item.mrp > item.price && (
                      <span className="block text-xs text-neutral-400 line-through">₹{(item.mrp * item.qty).toLocaleString("en-IN")}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
          <button onClick={clearCart} className="text-sm text-neutral-500 hover:text-[#D6425F] underline">Clear cart</button>
        </div>

        <div className="lg:col-span-1">
          <div className="bg-white border border-neutral-200 rounded-sm p-6 sticky top-28">
            <h2 className="font-serif text-xl text-neutral-900 mb-5">Order Summary</h2>
            <div className="flex gap-2 mb-2">
              <div className="flex-1 flex items-center border border-neutral-300 rounded-sm px-3">
                <Tag className="h-4 w-4 text-neutral-400" />
                <input value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="Coupon (try DEEG10)"
                  className="flex-1 px-2 py-2.5 text-sm focus:outline-none" />
              </div>
              <button onClick={applyCoupon}
                className="bg-gradient-to-br from-[#FFE29A] to-[#C8932F] text-[#050304] px-4 text-sm font-semibold rounded-sm hover:opacity-90 transition-opacity">
                Apply
              </button>
            </div>
            {couponMsg && <p className={`text-xs mb-4 ${discount > 0 ? "text-[#C8932F]" : "text-red-600"}`}>{couponMsg}</p>}

            <div className="space-y-2.5 text-sm border-t border-neutral-200 pt-4">
              <div className="flex justify-between"><span className="text-neutral-600">Subtotal</span><span>₹{cartTotal.toLocaleString("en-IN")}</span></div>
              {discount > 0 && <div className="flex justify-between text-[#D6425F]"><span>Coupon discount</span><span>-₹{discount.toLocaleString("en-IN")}</span></div>}
              <div className="flex justify-between"><span className="text-neutral-600">Shipping</span><span>{shipping === 0 ? "Free" : `₹${shipping}`}</span></div>
            </div>
            <div className="flex justify-between items-baseline border-t border-neutral-200 mt-4 pt-4">
              <span className="font-semibold text-neutral-900">Total</span>
              <span className="text-2xl font-bold text-neutral-900">₹{grandTotal.toLocaleString("en-IN")}</span>
            </div>
            {savings > 0 && <p className="text-xs text-[#D6425F] mt-1">You save ₹{savings.toLocaleString("en-IN")}</p>}

            <button onClick={() => navigate("/checkout")}
              className="mt-6 w-full bg-gradient-to-br from-[#FFE29A] to-[#C8932F] text-[#050304] py-3.5 text-sm font-semibold uppercase tracking-widest rounded-sm hover:opacity-90 transition-opacity">
              Proceed to Checkout
            </button>
            <button onClick={() => navigate("/collections/all")} className="mt-3 w-full text-sm text-neutral-600 hover:text-[#C8932F]">Continue Shopping</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
